import FormType from "enums/FormType";

import { LoadingSpinner } from "react/components/LoadingSpinner";
import {
    selectCanEditForm,
    selectHasEditPermissionsForAutomatedEvaluationForms,
} from "../qualityFormSlice";
import { toGuxDisabled } from "util/componentUtil";
import {
    selectForm,
    selectIsAutomatedEvaluationForm,
} from "../qualityFormSlices/qualityFormCommons";
import { selectHasAddPermissions } from "../qualityFormPermissionSelectors";
import { FormEditor } from "./FormEditor";
import { FormEditorSidePanel } from "./sidePanel/FormEditorSidePanel";

import "./FormEditorContainer.less";
import { useEffect, useState } from "react";
import FormViewerPreview from "./FormViewerPreview";
import { BottomBanner } from "./BottomBanner";
import { GuxToggle, GuxTruncate } from "genesys-spark-components-react";
import {
    scorableSliceActions,
    previewLoadScorable,
    getPreviewScores,
} from "features/scorable/scorableSlice";
import { selectIsTopicsLoading } from "features/topics/topicsSlice";
import strings from "./FormEditorContainer.strings";
import { useStrings } from "features/i18n/useStrings";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { isPreviewToggleDisabled } from "../qualityFormValidations";
import {
    PreviewModeContext,
    HasAddFormPermissionsContext,
} from "../common/Contexts";
import { selectIsInCopyingViewMode } from "./copyFormSlice";
import newRelicService from "services/newRelic/newRelicService";
import { NewRelicCustomAttributeName } from "services/newRelic/NewRelicEnum";
// Check src/services/newRelic/newRelicService.js for more information about New Relic.

import { selectCurrentOrgId } from "features/auth/currentUserSlice";
import {
    selectShouldLoadAgreementRate,
    selectFormAgreementRateLoading,
} from "../qualityFormAgreementRateSelectors";
import { selectAiScoringQuestionsMaxOuterLimitLoadStatus } from "../outerLimitSlice";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import FeatureToggle from "enums/FeatureToggle";

type FormEditorContainerProps = {
    formType: FormType;
};

export default function FormEditorContainer({
    formType,
}: FormEditorContainerProps) {
    const t = useStrings(strings);
    const isFormLoaded = useAppSelector(
        (state) => selectForm(state) !== undefined
    );
    const isTopicsLoading = useAppSelector(selectIsTopicsLoading);
    const canEditForm = useAppSelector(selectCanEditForm);
    const hasAddFormPermissions = useAppSelector(selectHasAddPermissions);
    const canEditAutomatedEvaluationForm = useAppSelector((state) => {
        return selectHasEditPermissionsForAutomatedEvaluationForms(state);
    });

    const isAutomatedEvaluationForm = useAppSelector(
        selectIsAutomatedEvaluationForm
    );

    const shouldLoadAgreementRate = useAppSelector(
        selectShouldLoadAgreementRate
    );
    const isAgreementRateLoading = useAppSelector(
        selectFormAgreementRateLoading
    );

    // Determine if we're currently waiting for agreement rate data to load
    const isWaitingForAgreementRate =
        shouldLoadAgreementRate && isAgreementRateLoading;

    const shouldWaitForTopics =
        formType === FormType.EVALUATION && isTopicsLoading;

    const isOuterLimitFTEnabled = useAppSelector((state) =>
        selectFeatureToggle(
            state,
            FeatureToggle.MAX_AI_SCORING_QUESTION_OUTER_LIMIT
        )
    );
    const outerLimitLoadStatus = useAppSelector(
        selectAiScoringQuestionsMaxOuterLimitLoadStatus
    );
    const shouldWaitForOuterLimit =
        formType === FormType.EVALUATION &&
        isOuterLimitFTEnabled &&
        (outerLimitLoadStatus === "idle" || outerLimitLoadStatus === "loading");

    // Form is ready to render when form data is loaded and we're not waiting for required agreement rate data
    const isReadyToRender =
        isFormLoaded &&
        !isWaitingForAgreementRate &&
        !shouldWaitForTopics &&
        !shouldWaitForOuterLimit;

    const displayTopBanner = formType === FormType.ASSESSMENT;
    const displayCustomMargin =
        formType === FormType.SURVEY || formType === FormType.EVALUATION;
    const displayBottomBanner =
        formType === FormType.SURVEY ||
        (formType === FormType.EVALUATION &&
            (!isAutomatedEvaluationForm || canEditAutomatedEvaluationForm));

    const isInCopyingViewMode = useAppSelector(selectIsInCopyingViewMode);

    const [previewMode, setPreviewMode] = useState(false);

    const dispatch = useAppDispatch();
    const isPreviewDisabled = useAppSelector(isPreviewToggleDisabled);
    function handlePreviewAnswers({ previewMode }: { previewMode: boolean }) {
        if (!previewMode) {
            dispatch(previewLoadScorable());
            dispatch(
                getPreviewScores({
                    isScorableFinished: false,
                    isInitialLoad: true,
                })
            );
        } else {
            dispatch(scorableSliceActions.clearScorable());
        }
        setPreviewMode(!previewMode);
    }

    const currentOrgId = useAppSelector(selectCurrentOrgId);

    useEffect(() => {
        newRelicService.setCustomAttribute(
            NewRelicCustomAttributeName.FORM_TYPE,
            formType ?? "undefined"
        );
        newRelicService.setCustomAttribute(
            NewRelicCustomAttributeName.ORGANIZATION_ID,
            currentOrgId ?? "undefined"
        );
    }, [currentOrgId, formType]);

    useEffect(() => {
        if (isInCopyingViewMode) {
            setPreviewMode(false);
            dispatch(scorableSliceActions.clearScorable());
        }
    }, [dispatch, isInCopyingViewMode]);

    return (
        <div
            className={`FormEditorContainer ${
                displayTopBanner ? "has-header" : ""
            } ${displayCustomMargin ? "has-custom-margin" : ""} ${
                displayBottomBanner ? "has-footer" : ""
            }`}
        >
            {isReadyToRender ? (
                <>
                    {displayTopBanner && (
                        <div className="top-banner">
                            <GuxTruncate maxLines={1}>
                                <h1 className="gux-heading-lg-bold">
                                    {previewMode
                                        ? t.labels.defaultAssessmentPreviewText
                                        : canEditForm
                                        ? t.labels.defaultAssessmentEditorText
                                        : t.labels
                                              .defaultPublishedAssessmentText}
                                </h1>
                            </GuxTruncate>
                        </div>
                    )}
                    <div className="main-panel">
                        {!previewMode ? (
                            <FormEditor formType={formType} />
                        ) : (
                            <FormViewerPreview />
                        )}
                    </div>
                    <div className="side-panel">
                        <div className="toggle-switch">
                            <GuxToggle
                                disabled={toGuxDisabled(isPreviewDisabled)}
                                aria-disabled={toGuxDisabled(isPreviewDisabled)}
                                className={isPreviewDisabled ? "" : "Clickable"}
                                checkedLabel={t.FlipSwitch.PreviewOn}
                                uncheckedLabel={t.FlipSwitch.PreviewOff}
                                checked={previewMode}
                                onCheck={() => {
                                    handlePreviewAnswers({ previewMode });
                                }}
                            />
                        </div>
                        <PreviewModeContext.Provider value={{ previewMode }}>
                            <FormEditorSidePanel />
                        </PreviewModeContext.Provider>
                    </div>
                    {displayBottomBanner && (
                        // TODO: Review why Context is used here.
                        <HasAddFormPermissionsContext.Provider
                            value={{ hasAddFormPermissions }}
                        >
                            <div
                                className="bottom-banner"
                                data-testid="bottom-banner"
                            >
                                <BottomBanner />
                            </div>
                        </HasAddFormPermissionsContext.Provider>
                    )}
                </>
            ) : (
                <LoadingSpinner />
            )}
        </div>
    );
}
